import { CSSProperties, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import isEqual from "lodash/isEqual";
import { ChangeEvent } from 'react';
import { styx } from 'modules/Utils';

interface Option {
  readonly label: string;
  readonly value: string;
}

interface ColumnOption {
  id: string;
  label: string;
}

interface CreatableSelectColumnProps {
  currentMatch: string;
  defaultValue: string;
  initialValue: Option | null;
  availableOptions: ColumnOption[];
  handleChangeInput: (e: ChangeEvent<{ value: unknown; name?: string }>) => void;
  headerLabel: string;
}

const CreatableSelectColumn = ({
  currentMatch,
  defaultValue,
  availableOptions,
  initialValue,
  handleChangeInput,
  headerLabel
}: CreatableSelectColumnProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<Option | null>(initialValue);

  const handleCreate = (inputValue: string) => {
    setIsLoading(true);
    setTimeout(() => {
      // Sanitize input by trimming whitespace and removing special characters
      const sanitizedValue = inputValue
        .trim()
        .replace(/[^a-zA-Z0-9\s]/g, '_') // Replace special characters with underscore
      
      const newOption = {
        value: sanitizedValue,
        label: inputValue.trim() // Preserve original case but trim whitespace
      };
      
      setIsLoading(false);
      setValue(newOption);
      handleChangeInput({
        target: { name: newOption.value, value: headerLabel }
      } as ChangeEvent<{ name: string; value: unknown }>);
    }, 1000);
  };

  return (
    <CreatableSelect<Option>
      styles={{
        control: (baseStyles, state) => ({
          ...baseStyles,
          ...styx(
            sty.container,
            { color: isEqual(currentMatch, defaultValue) ? '#707070' : '#000000' }
          )
        }),
      }}
      options={availableOptions.map(opt => ({
        value: opt.id,
        label: opt.label
      }))}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        handleChangeInput({
          target: { name: newValue?.value || defaultValue, value: headerLabel }
        } as ChangeEvent<{ name: string; value: unknown }>);
      }}
      onCreateOption={handleCreate}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      placeholder="Select or enter a column..."
      isClearable
      isDisabled={isLoading}
      isLoading={isLoading}
    />
  );
};

const sty: Record<string, CSSProperties>  = {
  container: {
    width: 300,
    flexShrink: 0,
    flexGrow: 0,
  }
};

export default CreatableSelectColumn;